// src/services/SecureTokenStorage.tsx

import { setCookie, getCookie, deleteCookie } from './CookieService';

// Token storage configuration
const TOKEN_CONFIG = {
  // For HttpOnly cookies, these would need to be set by the server
  // But we can implement the most secure client-side alternative
  secure: window.location.protocol === 'https:', // Only secure in production/HTTPS
  sameSite: 'lax' as const, // Allow cookies during navigation (needed for Keycloak redirects)
  path: '/',
  // Tokens expire in 15 minutes for security
  tokenExpirationMinutes: 15,
  // ACR tokens expire in 5 minutes for enhanced security
  acrTokenExpirationMinutes: 5,
};

// Cookie names for different tokens (conditional prefix based on environment)
const isSecure = window.location.protocol === 'https:';
const COOKIE_NAMES = {
  ACCESS_TOKEN: isSecure ? '__Secure-AccessToken' : 'AccessToken',
  ACR_TOKEN: isSecure ? '__Secure-AcrToken' : 'AcrToken', 
  REFRESH_TOKEN: isSecure ? '__Secure-RefreshToken' : 'RefreshToken',
  DOCUMENT_ID: isSecure ? '__Secure-DocumentId' : 'DocumentId',
  USER_ID: isSecure ? '__Secure-UserId' : 'UserId',
  CURRENT_AUTHORITY: isSecure ? '__Secure-CurrentAuthority' : 'CurrentAuthority',
  SIGNATURE_SVG: isSecure ? '__Secure-SignatureSvg' : 'SignatureSvg',
} as const;

// Encryption helper (simple XOR for basic obfuscation)
// Note: For production, use a proper encryption library
const simpleEncrypt = (text: string): string => {
  const key = 'SecureFinancialApp2025'; // In production, this should be from environment
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(result);
};

const simpleDecrypt = (encrypted: string): string => {
  try {
    const key = 'SecureFinancialApp2025';
    const text = atob(encrypted);
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  } catch (error) {
    console.error('Failed to decrypt token:', error);
    return '';
  }
};

// Calculate expiration date
const getExpirationDate = (minutes: number): Date => {
  const date = new Date();
  date.setTime(date.getTime() + (minutes * 60 * 1000));
  return date;
};

export class SecureTokenStorage {
  
  // Store access token securely
  static setAccessToken(token: string): void {
    if (!token) return;
    
    const encrypted = simpleEncrypt(token);
    setCookie(COOKIE_NAMES.ACCESS_TOKEN, encrypted, {
      expires: getExpirationDate(TOKEN_CONFIG.tokenExpirationMinutes),
      secure: TOKEN_CONFIG.secure,
      sameSite: TOKEN_CONFIG.sameSite,
      path: TOKEN_CONFIG.path,
    });
  }

  // Retrieve access token
  static getAccessToken(): string | null {
    const encrypted = getCookie(COOKIE_NAMES.ACCESS_TOKEN);
    if (!encrypted) return null;
    
    const decrypted = simpleDecrypt(encrypted);
    return decrypted || null;
  }

  // Store ACR token with shorter expiration
  static setAcrToken(token: string): void {
    if (!token) return;
    
    const encrypted = simpleEncrypt(token);
    setCookie(COOKIE_NAMES.ACR_TOKEN, encrypted, {
      expires: getExpirationDate(TOKEN_CONFIG.acrTokenExpirationMinutes),
      secure: TOKEN_CONFIG.secure,
      sameSite: TOKEN_CONFIG.sameSite,
      path: TOKEN_CONFIG.path,
    });
  }

  // Retrieve ACR token
  static getAcrToken(): string | null {
    const encrypted = getCookie(COOKIE_NAMES.ACR_TOKEN);
    if (!encrypted) return null;
    
    const decrypted = simpleDecrypt(encrypted);
    return decrypted || null;
  }

  // Store refresh token (most sensitive)
  static setRefreshToken(token: string): void {
    if (!token) return;
    
    const encrypted = simpleEncrypt(token);
    setCookie(COOKIE_NAMES.REFRESH_TOKEN, encrypted, {
      expires: getExpirationDate(TOKEN_CONFIG.tokenExpirationMinutes * 4), // Longer expiration
      secure: TOKEN_CONFIG.secure,
      sameSite: TOKEN_CONFIG.sameSite,
      path: TOKEN_CONFIG.path,
    });
  }

  // Retrieve refresh token
  static getRefreshToken(): string | null {
    const encrypted = getCookie(COOKIE_NAMES.REFRESH_TOKEN);
    if (!encrypted) return null;
    
    const decrypted = simpleDecrypt(encrypted);
    return decrypted || null;
  }

  // Store document ID
  static setDocumentId(id: string): void {
    if (!id) return;
    
    setCookie(COOKIE_NAMES.DOCUMENT_ID, id, {
      expires: getExpirationDate(TOKEN_CONFIG.tokenExpirationMinutes),
      secure: TOKEN_CONFIG.secure,
      sameSite: TOKEN_CONFIG.sameSite,
      path: TOKEN_CONFIG.path,
    });
  }

  // Retrieve document ID
  static getDocumentId(): string | null {
    return getCookie(COOKIE_NAMES.DOCUMENT_ID);
  }

  // Store user ID
  static setUserId(id: string): void {
    if (!id) return;
    
    setCookie(COOKIE_NAMES.USER_ID, id, {
      expires: getExpirationDate(TOKEN_CONFIG.tokenExpirationMinutes),
      secure: TOKEN_CONFIG.secure,
      sameSite: TOKEN_CONFIG.sameSite,
      path: TOKEN_CONFIG.path,
    });
  }

  // Retrieve user ID
  static getUserId(): string | null {
    return getCookie(COOKIE_NAMES.USER_ID);
  }

  // Store current authority
  static setCurrentAuthority(authority: string): void {
    if (!authority) return;
    
    setCookie(COOKIE_NAMES.CURRENT_AUTHORITY, authority, {
      expires: getExpirationDate(TOKEN_CONFIG.tokenExpirationMinutes),
      secure: TOKEN_CONFIG.secure,
      sameSite: TOKEN_CONFIG.sameSite,
      path: TOKEN_CONFIG.path,
    });
  }

  // Retrieve current authority
  static getCurrentAuthority(): string | null {
    return getCookie(COOKIE_NAMES.CURRENT_AUTHORITY);
  }

  // Store signature SVG (using localStorage for large data due to cookie size limits)
  static setSignatureSvg(svg: string): void {
    if (!svg) return;
    
    // console.log('SecureTokenStorage.setSignatureSvg - Original SVG length:', svg.length);
    // console.log('SecureTokenStorage.setSignatureSvg - Original SVG preview:', svg.substring(0, 100));
    
    // Use base64 encoding for consistency
    const encoded = btoa(unescape(encodeURIComponent(svg)));
    // console.log('SecureTokenStorage.setSignatureSvg - Encoded length:', encoded.length);
    
    // SVGs are often too large for cookies (4KB limit), so use localStorage as fallback
    if (encoded.length > 3000) {
      // console.log('SecureTokenStorage.setSignatureSvg - SVG too large for cookie, using localStorage');
      try {
        localStorage.setItem(COOKIE_NAMES.SIGNATURE_SVG, encoded);
        // console.log('SecureTokenStorage.setSignatureSvg - Stored in localStorage successfully');
      } catch (error) {
        console.error('SecureTokenStorage.setSignatureSvg - Failed to store in localStorage:', error);
      }
    } else {
      // console.log('SecureTokenStorage.setSignatureSvg - SVG small enough for cookie');
      const cookieOptions = {
        expires: getExpirationDate(TOKEN_CONFIG.tokenExpirationMinutes),
        secure: TOKEN_CONFIG.secure,
        sameSite: TOKEN_CONFIG.sameSite,
        path: TOKEN_CONFIG.path,
      };
      
      setCookie(COOKIE_NAMES.SIGNATURE_SVG, encoded, cookieOptions);
    }
    
    // Verify storage
    const verification = this.getSignatureSvg();
    // console.log('SecureTokenStorage.setSignatureSvg - Verification result length:', verification?.length || 'null');
  }

  // Retrieve signature SVG (check both localStorage and cookies)
  static getSignatureSvg(): string | null {
    // First try cookie
    let encoded = getCookie(COOKIE_NAMES.SIGNATURE_SVG);
    let source = 'cookie';
    
    // If not in cookie, try localStorage
    if (!encoded) {
      encoded = localStorage.getItem(COOKIE_NAMES.SIGNATURE_SVG);
      source = 'localStorage';
    }
    
    if (!encoded) {
      // console.log('SecureTokenStorage.getSignatureSvg - No encoded data found in cookie or localStorage');
      return null;
    }
    
    // console.log(`SecureTokenStorage.getSignatureSvg - Found in ${source}, encoded length:`, encoded.length);
    
    try {
      // Use base64 decoding
      const decoded = decodeURIComponent(escape(atob(encoded)));
      // console.log('SecureTokenStorage.getSignatureSvg - Decoded length:', decoded?.length || 'null');
      // console.log('SecureTokenStorage.getSignatureSvg - Decoded preview:', decoded?.substring(0, 100) || 'null');
      
      return decoded || null;
    } catch (error) {
      console.error(`Failed to decode SVG from ${source}:`, error);
      return null;
    }
  }

  // Clear individual tokens
  static clearDocumentId(): void {
    deleteCookie(COOKIE_NAMES.DOCUMENT_ID, { path: TOKEN_CONFIG.path });
  }

  static clearCurrentAuthority(): void {
    deleteCookie(COOKIE_NAMES.CURRENT_AUTHORITY, { path: TOKEN_CONFIG.path });
  }

  static clearAcrToken(): void {
    deleteCookie(COOKIE_NAMES.ACR_TOKEN, { path: TOKEN_CONFIG.path });
  }

  static clearUserId(): void {
    deleteCookie(COOKIE_NAMES.USER_ID, { path: TOKEN_CONFIG.path });
  }

  static clearSignatureSvg(): void {
    deleteCookie(COOKIE_NAMES.SIGNATURE_SVG, { path: TOKEN_CONFIG.path });
    localStorage.removeItem(COOKIE_NAMES.SIGNATURE_SVG);
  }

  // Clear all tokens (logout)
  static clearAllTokens(): void {
    Object.values(COOKIE_NAMES).forEach(cookieName => {
      deleteCookie(cookieName, { path: TOKEN_CONFIG.path });
    });
  }

  // Check if access token exists and is valid
  static hasValidAccessToken(): boolean {
    const token = this.getAccessToken();
    return !!token && token.length > 0;
  }

  // Check if ACR token exists and is valid
  static hasValidAcrToken(): boolean {
    const token = this.getAcrToken();
    return !!token && token.length > 0;
  }

  // Migrate data from localStorage to secure cookies (one-time migration)
  static migrateFromLocalStorage(): void {
    try {
      // Migrate ACR token
      const acrToken = localStorage.getItem('AcrToken');
      if (acrToken) {
        this.setAcrToken(acrToken);
        localStorage.removeItem('AcrToken');
      }

      // Migrate document ID
      const documentId = localStorage.getItem('currentDocumentId');
      if (documentId) {
        this.setDocumentId(documentId);
        localStorage.removeItem('currentDocumentId');
      }

      // Migrate user ID
      const userId = localStorage.getItem('userId');
      if (userId) {
        this.setUserId(userId);
        localStorage.removeItem('userId');
      }

      // Migrate current authority
      const authority = localStorage.getItem('currentAuthority');
      if (authority) {
        this.setCurrentAuthority(authority);
        localStorage.removeItem('currentAuthority');
      }

      // Migrate signature SVG
      const signatureSvg = localStorage.getItem('signatureSvg');
      if (signatureSvg) {
        this.setSignatureSvg(signatureSvg);
        localStorage.removeItem('signatureSvg');
      }

      console.info('Successfully migrated tokens from localStorage to secure cookies');
    } catch (error) {
      console.error('Error during localStorage migration:', error);
    }
  }
}

export default SecureTokenStorage;